import { c as _c } from "react/compiler-runtime";
import type { RiskSignal, RiskSignalGroupKind } from '@onefootprint/request-types/dashboard';
import { Card } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import getSignalFamily, { type SignalFamily } from '../../../../utils/get-signal-family';
import CardTitle, { CardStatus } from '../../../card-title';
import RiskSignalItem, { SEVERITY_TONE } from '../risk-signal-item';

// Families that hold a signal or two each add more headings than they save scanning.
const MIN_SIGNALS_TO_SPLIT = 8;
const MIN_AVERAGE_SIGNALS_PER_FAMILY = 3;
type Block = {
  family?: SignalFamily;
  signals: RiskSignal[];
};

// Signals arrive most severe first, so families come out ordered by their worst signal.
const getBlocks = (group: RiskSignalGroupKind, signals: RiskSignal[]): Block[] => {
  const families = new Map<SignalFamily, RiskSignal[]>();
  signals.forEach(signal => {
    const family = getSignalFamily(group, signal.reasonCode);
    if (family) {
      const familySignals = families.get(family);
      if (familySignals) {
        familySignals.push(signal);
      } else {
        families.set(family, [signal]);
      }
    }
  });
  if (signals.length < MIN_SIGNALS_TO_SPLIT || families.size < 2 || signals.length / families.size < MIN_AVERAGE_SIGNALS_PER_FAMILY) {
    return [{
      signals
    }];
  }
  return Array.from(families, ([family, familySignals]) => ({
    family,
    signals: familySignals
  }));
};
type RiskSignalGroupProps = {
  title: string;
  group: RiskSignalGroupKind;
  signals: RiskSignal[];
};
const RiskSignalGroup = t0 => {
  const $ = _c(28);
  const {
    title,
    group,
    signals
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "onboardings.riskSignals"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  let T0;
  let T1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[1] !== group || $[2] !== signals || $[3] !== t || $[4] !== title) {
    const flags = signals.filter(_temp);
    const blocks = getBlocks(group, signals);
    T1 = Card.Root;
    t4 = "group";
    t5 = title;
    const t7 = flags.length ? <CardStatus tone={SEVERITY_TONE[flags[0].severity]}>{flags.length === 1 ? t(_temp2, {
        count: 1
      }) : t(_temp3, {
        count: flags.length
      })}</CardStatus> : <CardStatus tone="success">{t(_temp4)}</CardStatus>;
    if ($[12] !== signals.length || $[13] !== t7 || $[14] !== title) {
      t6 = <CardTitle title={title} count={signals.length} rightContent={t7} />;
      $[12] = signals.length;
      $[13] = t7;
      $[14] = title;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    T0 = Card.Body;
    t2 = "flex flex-col p-2";
    let t8;
    if ($[16] !== t) {
      t8 = (t9, index) => {
        const {
          family,
          signals: blockSignals
        } = t9;
        const label = family && t($_2 => $_2.families[family]);
        return <div key={family ?? "all"} className="flex flex-col" {...family ? {
          role: "group",
          "aria-label": label
        } : {}}>{index > 0 && <div aria-hidden={true} className="mx-2 mt-4 border-t border-tertiary" />}{family && <h6 className={cx("px-2 pb-2 text-label-2 text-primary", index > 0 ? "pt-4" : "pt-2.5")}>{label}</h6>}<ul className="flex flex-col gap-0.5">{blockSignals.map(_temp5)}</ul></div>;
      };
      $[16] = t;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    t3 = blocks.map(t8);
    $[1] = group;
    $[2] = signals;
    $[3] = t;
    $[4] = title;
    $[5] = T0;
    $[6] = T1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    T0 = $[5];
    T1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[18] !== T0 || $[19] !== t2 || $[20] !== t3) {
    t7 = <T0 className={t2}>{t3}</T0>;
    $[18] = T0;
    $[19] = t2;
    $[20] = t3;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== T1 || $[23] !== t4 || $[24] !== t5 || $[25] !== t6 || $[26] !== t7) {
    t8 = <T1 role={t4} aria-label={t5}>{t6}{t7}</T1>;
    $[22] = T1;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
};
export default RiskSignalGroup;
function _temp(t0) {
  const {
    severity
  } = t0;
  return severity !== "info";
}
function _temp2($) {
  return $.flags_one;
}
function _temp3($_0) {
  return $_0.flags_other;
}
function _temp4($_1) {
  return $_1.noFlags;
}
function _temp5(signal) {
  return <li key={signal.id}><RiskSignalItem riskSignal={signal} /></li>;
}